import type React from 'react';
import { useState } from 'react';
import { useLeasingUrlParams } from '../../../../hooks/useLeasingUrlParams';
import styled from 'styled-components';

import {
  ButtonGroup,
  Card,
  DialogActions,
  Flex,
  FormField,
  FormHelp,
  Heading,
  IconButton,
  IconRenameOutline,
  IconTrashcanOutline,
  Image,
  Input,
  Label,
  Paragraph,
  Popover,
  Popper,
  TextButton,
} from '@zillow/constellation';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';
import type { DocumentDetailsDto } from '@zg-rentals/ts-leasing-client';
import {
  useDeleteLeaseDocumentMutation,
  useRenameLeaseDocumentsMutation,
  useUploadedFileDetailsQuery,
} from '../../../../utils/queries';

const StyledImage = styled(Image)`
  object-fit: contain;
  max-height: 3rem;
  max-width: 4rem;
  width: 100%;
`;

const StyledParagraph = styled(Paragraph)`
  word-break: break-all;
`;

type Props = {
  leaseDetail: DocumentDetailsDto;
};

function DocumentCard({ leaseDetail }: Props) {
  const { leaseId } = useLeasingUrlParams();
  const { name } = leaseDetail;
  const [documentName, setDocumentName] = useState(name);

  const deleteMutation = useDeleteLeaseDocumentMutation();
  const renameMutation = useRenameLeaseDocumentsMutation();
  const uploadedFilesQuery = useUploadedFileDetailsQuery(Number(leaseId));

  const isMutating = deleteMutation.isPending || renameMutation.isPending || uploadedFilesQuery.isFetching;

  async function onRenameSave() {
    await renameMutation.mutateAsync({
      postData: {
        leaseId: Number(leaseId),
        renames: [{ documentId: leaseDetail.id, name: documentName }],
      },
    });
  }

  async function onDeleteClick() {
    await deleteMutation.mutateAsync({
      postData: {
        leaseId: Number(leaseId),
        documentId: leaseDetail.id,
      },
    });
  }

  function renamePopover(props, close): React.ReactNode {
    return (
      <Popover
        {...props}
        header={<Heading level={1}>Rename document</Heading>}
        body={
          <>
            <FormField
              className={FULLSTORY_EXCLUDE}
              required
              label={<Label>Name</Label>}
              control={<Input value={documentName} onChange={(e) => setDocumentName(e.target.value)} />}
              description={<FormHelp>Your tenant will see this file name when signing the document</FormHelp>}
            />
            <DialogActions>
              <TextButton
                buttonType="primary"
                disabled={!documentName.trim() || isMutating}
                onClick={() => {
                  onRenameSave();
                  close();
                }}
              >
                Save
              </TextButton>
            </DialogActions>
          </>
        }
      />
    );
  }

  return (
    <>
      <Card cardType="secondary" cardElevation={0}>
        <Flex display="flex" justifyContent="space-between" alignItems="center">
          <Flex
            display="flex"
            alignItems="center"
            justifyContent="center"
            marginRight="md"
            style={{ minWidth: '4rem', maxWidth: '4rem' }}
          >
            <StyledImage alt="Document preview" src={leaseDetail.imageUrl} />
          </Flex>
          <Flex flex="1" marginRight="md">
            <StyledParagraph>{leaseDetail.name}</StyledParagraph>
          </Flex>
          <ButtonGroup aria-label="document options">
            <Popper triggered={renamePopover}>
              {(props) => (
                <IconButton
                  bare
                  appearance="circle"
                  title="Rename"
                  buttonType="secondary"
                  icon={<IconRenameOutline />}
                  disabled={isMutating}
                  {...props}
                />
              )}
            </Popper>
            <IconButton
              bare
              appearance="circle"
              title="Delete"
              buttonType="secondary"
              icon={<IconTrashcanOutline />}
              onClick={onDeleteClick}
              disabled={isMutating}
            />
          </ButtonGroup>
        </Flex>
      </Card>
    </>
  );
}

export default DocumentCard;
